import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LogOut, Mail, Phone } from "lucide-react";

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    (async () => {
      try {
        const { data } = await api.get("/admin/inquiries", { withCredentials: true });
        setRows(data);
      } catch (e) {
        if (e.response?.status === 401) { navigate("/"); return; }
        setErr(e.response?.status === 403 ? "Not an admin account." : "Failed to load.");
      }
    })();
  }, [user, loading, navigate]);

  if (loading || !user) {
    return <div className="min-h-screen grid place-items-center bg-nova-bg text-nova-mute mono-label">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-nova-bg text-nova-text px-6 lg:px-10 py-10">
      <div className="max-w-6xl mx-auto">
        <header className="flex items-center justify-between mb-10 border-b border-nova-line pb-6">
          <div>
            <div className="mono-label">[ Admin console ]</div>
            <h1 className="font-display text-3xl mt-1">Inquiries</h1>
          </div>
          <div className="flex items-center gap-4">
            {user.picture && <img src={user.picture} alt="" className="w-9 h-9 rounded-full" />}
            <div className="text-right">
              <div className="text-sm">{user.name}</div>
              <div className="mono-label">{user.email}</div>
            </div>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 border border-nova-line hover:border-nova-gold/40 transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </header>

        {err && <div className="text-red-400 mb-6" data-testid="admin-error">{err}</div>}

        {!err && rows.length === 0 && <div className="text-nova-mute">No inquiries yet.</div>}

        <div className="space-y-4" data-testid="admin-inquiries-list">
          {rows.map((r) => (
            <article key={r.id} className="border border-nova-line p-6 hover:border-nova-gold/40 transition-colors">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="font-display text-xl">{r.full_name} <span className="text-nova-mute text-sm">· {r.service_interest || "—"}</span></div>
                <div className="mono-label">{new Date(r.created_at).toLocaleString()}</div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3 text-sm text-nova-mute mb-3">
                <a href={`mailto:${r.email}`} className="flex items-center gap-2 hover:text-nova-gold"><Mail className="w-3.5 h-3.5" />{r.email}</a>
                <a href={`tel:${r.phone}`} className="flex items-center gap-2 hover:text-nova-gold"><Phone className="w-3.5 h-3.5" />{r.phone}</a>
                <span>Budget: {r.budget || "—"}</span>
              </div>
              <p className="text-nova-text whitespace-pre-wrap">{r.message}</p>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
